import * as React from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'gatsby';

import DropdownMenu from '@src/components/Nav/DropdownMenu';
import DropdownMenuItem from '@src/components/Nav/DropdownMenuItem';
import {NavItem} from '@src/components/Nav';
import {GTMCallUs} from '@src/components/GTM/GTMCallUs';

import Logo from '@src/svgs/Logo';

import * as styles from './desktopNav.module.scss';

import type {Translations} from '@src/i18n';
import Button from '@src/components/Button';
import LanguageToggle from '../LanguageToggle';

const GTM_DEFAULT_KEYS = {
  phoneNumber: 'callUs.number',
  text: 'callUs.text',
};

const GTM_DIRECT_KEYS = {
  phoneNumber: 'callUsDirect.number',
  text: 'callUsDirect.text',
};

const GTM_CALL_US_PROPS = {
  defaultKeys: GTM_DEFAULT_KEYS,
  directKeys: GTM_DIRECT_KEYS,
  namespace: 'Nav',
};

interface DesktopNavProps {
  translations?: Translations[];
  nav: NavItem[];
}

const DesktopNav: React.FC<DesktopNavProps> = ({translations, nav}) => {
  const {i18n, t} = useTranslation('Nav');

  return (
    <nav className={styles.nav} role="navigation">
      <div className={styles.logoBar}>
        <div className={styles.logo}>
          <Link className={styles.logoLink} to={t('links.home.url')}>
            <span className={styles.visuallyHidden}>
              {t('links.home.text')}
            </span>
            <Logo />
          </Link>
        </div>
        <div className={styles.cta}>
          <GTMCallUs {...GTM_CALL_US_PROPS}>
            {({text, phoneNumber}) => (
              <>
                <span className={styles.hours}>{text}</span>
                <a className={styles.phoneNumber}>
                  {phoneNumber}
                  <span> TTY 711</span>
                </a>
              </>
            )}
          </GTMCallUs>
        </div>
      </div>
      <div className={styles.linkBar}>
        <Link
          activeClassName={styles.isActive}
          className={styles.link}
          to={t('links.providers.url')}
          onClick={() => {
            i18n.changeLanguage('en-us');
          }}>
          {t('links.providers.text')}
        </Link>
        <Link
          activeClassName={styles.isActive}
          className={styles.link}
          to={t('links.brokers.url')}
          onClick={() => {
            i18n.changeLanguage('en-us');
          }}>
          {t('links.brokers.text')}
        </Link>
        <LanguageToggle className={styles.link} translations={translations} />
      </div>
      <div className={styles.primary}>
        <ul className={styles.list}>
          <DesktopNavItems nav={nav} />
        </ul>
        <div className={styles.loginButtonWrapper}>
          <Link to="https://my.devoted.com/">
            <Button size="medium" text={t('memberLogin')} theme="secondary" />
          </Link>
        </div>
      </div>
    </nav>
  );
};

interface DesktopNavItemsProps {
  nav: NavItem[];
}

const DesktopNavItems: React.FC<DesktopNavItemsProps> = ({nav}) => {
  const [activeMenu, setActiveMenu] = React.useState(-1);
  const setIsOpenFor = (i: number) => (v: boolean) => {
    // if menu is opened, it becomes the active menu
    // if menu is closed, there is no active menu
    setActiveMenu(v ? i : -1);
  };

  return (
    <>
      {nav?.map(({primary, items}: NavItem, i) => (
        <li key={i} className={styles.listItem}>
          {items.length ? (
            <DropdownMenu
              isOpen={activeMenu === i}
              setIsOpen={setIsOpenFor(i)}
              title={primary.label.text}>
              <ul role="menu">
                {items.map((item, idx) => (
                  <li key={idx}>
                    <DropdownMenuItem item={item} />
                  </li>
                ))}
              </ul>
            </DropdownMenu>
          ) : (
            <Link
              activeClassName={styles.isActive}
              className={styles.link}
              to={primary.link.text}>
              {primary.label.text}
            </Link>
          )}
        </li>
      ))}
    </>
  );
};

export default DesktopNav;
